import type { FormOption, SlotOptions } from "@/lib/api/types";
import { floorOf, hasRoom } from "@/lib/appointments";

/** One row of a start list, as typed. Entrants are names until submit. */
export type GridRow = {
  entrant: string;
  values: Record<string, string>;
};

export function blankRow(): GridRow {
  return { entrant: "", values: {} };
}

/**
 * The key a connection column stores its value under.
 *
 * Namespaced so it cannot collide with a declared entry field: a sport is free
 * to have a field called `jockey`, and it would otherwise overwrite the jockey
 * column or be overwritten by it.
 */
export function connectionKey(role: string, index: number): string {
  return `@${role}:${index}`;
}

/**
 * How many columns each connection slot gets.
 *
 * A racecard is a spreadsheet with a jockey column and a trainer column, so
 * that is what the grid draws. A slot taking several people gets several
 * columns — Owner 1, Owner 2 — because the alternative was the grid silently
 * having no room for them at all, which is how a card could be entered with
 * every runner's connections quietly missing.
 */
export function connectionColumns(
  slots: SlotOptions[],
  extra: Record<string, number> = {},
): { slot: SlotOptions; index: number; key: string; label: string }[] {
  return slots.flatMap((slot) => {
    const count = Math.max(floorOf(slot), (extra[slot.role] ?? 0) + 1);
    const capped = slot.max === null || slot.max === undefined ? count : Math.min(count, slot.max);
    return Array.from({ length: capped }, (_, index) => ({
      slot,
      index,
      key: connectionKey(slot.role, index),
      // Numbered only when there is more than one, so "Jockey" stays "Jockey".
      label: capped === 1 ? slot.label : `${slot.label} ${index + 1}`,
    }));
  });
}

/** Whether another column would fit for this slot. */
export function canAddColumn(slot: SlotOptions, extra: Record<string, number>): boolean {
  const shown = Math.max(floorOf(slot), (extra[slot.role] ?? 0) + 1);
  return hasRoom(slot, Array<string>(shown).fill(""));
}

/**
 * A block pasted out of a spreadsheet, landing where the cursor is.
 *
 * Tab-separated columns, newline-separated rows — what every spreadsheet puts
 * on the clipboard. Rows are added as needed, so twenty runners can be pasted
 * into an empty grid without preparing twenty rows first, and a paste wider
 * than the grid drops what does not fit rather than inventing columns.
 *
 * Pure, and here rather than in the component, because the edge cases are where
 * this earns its keep: trailing blank lines from a copied range, Windows line
 * endings, a single cell that should behave like ordinary typing.
 */
export function applyPaste(
  rows: GridRow[],
  row: number,
  column: number,
  columns: string[],
  text: string,
): GridRow[] {
  const block = text
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .filter((line) => line.trim() !== "")
    .map((line) => line.split("\t"));

  const next = rows.map((r) => ({ ...r, values: { ...r.values } }));
  block.forEach((line, dr) => {
    const target = row + dr;
    while (next.length <= target) next.push(blankRow());
    line.forEach((cell, dc) => {
      const key = columns[column + dc];
      if (key === undefined) return;
      if (column + dc === 0) next[target].entrant = cell.trim();
      else next[target].values[key] = cell.trim();
    });
  });
  // Always a spare row at the bottom, so carrying on never needs a button.
  if (next.length === 0 || next[next.length - 1].entrant !== "") next.push(blankRow());
  return next;
}

/**
 * Turn typed names into participant codes.
 *
 * An unmatched name is returned as itself rather than dropped, so the server
 * rejects it by name — "unknown participant 'Rohit Sharm'" — instead of a
 * runner quietly going missing from a card that looked complete.
 */
export function resolveEntrants(rows: GridRow[], entrants: FormOption[]): GridRow[] {
  const byName = new Map(entrants.map((o) => [o.name.toLowerCase(), o.code]));
  const byCode = new Set(entrants.map((o) => o.code));
  return rows
    .filter((r) => r.entrant.trim() !== "")
    .map((r) => {
      const typed = r.entrant.trim();
      if (byCode.has(typed)) return r;
      return { ...r, entrant: byName.get(typed.toLowerCase()) ?? typed };
    });
}

/**
 * One row's connection columns, as the API takes them.
 *
 * Names resolve to codes the same way an entrant's does, and an unmatched one
 * travels as itself so the server names it back. Blanks are dropped: a card is
 * routinely filed before every jockey is booked.
 */
export function resolveConnections(
  row: GridRow,
  columns: { slot: SlotOptions; key: string }[],
): { role: string; participant: string }[] {
  const out: { role: string; participant: string }[] = [];
  for (const column of columns) {
    const typed = (row.values[column.key] ?? "").trim();
    if (typed === "") continue;
    const options = column.slot.options;
    const known = options.some((o) => o.code === typed);
    const matched = options.find((o) => o.name.toLowerCase() === typed.toLowerCase());
    out.push({
      role: column.slot.role,
      participant: known ? typed : (matched?.code ?? typed),
    });
  }
  return out;
}
